/**
 * Runtime i18n helper — nested message trees.
 * Resolve order: app lang → shared lang → app EN → shared EN
 */

import {
  INITIAL_ROWS,
  INITIAL_VERSIONS,
  SHARED_APP,
  buildNestedMessages,
  LANGUAGES,
  APPLICATIONS,
} from "./mockData"

const DEFAULT_LANG = "en"
const APP_CODES = APPLICATIONS.map((a) => a.code)

let currentApp = "portal"
let currentLang = DEFAULT_LANG
let versions = { ...INITIAL_VERSIONS }
/** @type {Record<string, Record<string, object>>} app → lang → nested tree */
let catalogs = {}

function ensureCatalog(app, lang) {
  if (!catalogs[app]) catalogs[app] = {}
  if (!catalogs[app][lang]) {
    catalogs[app][lang] = buildNestedMessages(INITIAL_ROWS, app, lang)
  }
  return catalogs[app][lang]
}

function resolvePath(tree, path) {
  if (!tree || !path) return undefined
  const parts = String(path).split(".").filter(Boolean)
  let cur = tree
  for (const p of parts) {
    if (cur == null || typeof cur !== "object") return undefined
    cur = cur[p]
  }
  return typeof cur === "string" ? cur : undefined
}

/** App catalog first, then shared (unless already on shared). */
function lookupChain(key, lang) {
  const apps =
    currentApp === SHARED_APP ? [SHARED_APP] : [currentApp, SHARED_APP]
  for (const app of apps) {
    const value = resolvePath(ensureCatalog(app, lang), key)
    if (value != null) return { value, source: app }
  }
  return null
}

function interpolate(str, vars) {
  if (!vars || typeof vars !== "object") return str
  return str.replace(/\{\{(\w+)\}\}/g, (_, k) =>
    vars[k] != null ? String(vars[k]) : `{{${k}}}`
  )
}

/**
 * Translate a dotted path: t("menu.settings") / t("common.actions.save", "Save")
 * Second arg may be a fallback string or an interpolation map.
 * Third arg is fallback when second is vars.
 */
export function t(key, fallbackOrVars, maybeFallback) {
  let vars = null
  let fallback = key

  if (typeof fallbackOrVars === "string") {
    fallback = fallbackOrVars
  } else if (fallbackOrVars && typeof fallbackOrVars === "object") {
    vars = fallbackOrVars
    if (typeof maybeFallback === "string") fallback = maybeFallback
  }

  const primary = lookupChain(key, currentLang)
  if (primary) return interpolate(primary.value, vars)

  if (currentLang !== DEFAULT_LANG) {
    const en = lookupChain(key, DEFAULT_LANG)
    if (en) return interpolate(en.value, vars)
  }

  return interpolate(fallback, vars)
}

/** Which catalog provided the string (app | shared), or null if missing. */
export function tSource(key) {
  const primary = lookupChain(key, currentLang)
  if (primary) return primary.source
  if (currentLang !== DEFAULT_LANG) {
    const en = lookupChain(key, DEFAULT_LANG)
    if (en) return en.source
  }
  return null
}

export function setLanguage(code) {
  const ok = LANGUAGES.some((l) => l.code === code && l.enabled)
  currentLang = ok ? code : DEFAULT_LANG
  return currentLang
}

export function getLanguage() {
  return currentLang
}

export function setApplication(code) {
  currentApp = code || "portal"
  return currentApp
}

export function getApplication() {
  return currentApp
}

export function getVersion(app = currentApp) {
  return versions[app] ?? 1
}

/** Replace catalog for app+lang (e.g. after admin edit or API fetch). */
export function loadMessages(app, lang, nestedTree, version) {
  if (!catalogs[app]) catalogs[app] = {}
  catalogs[app][lang] = nestedTree || {}
  if (version != null) versions[app] = version
}

/** Rebuild all language catalogs for an app from flat admin rows. */
export function hydrateFromRows(rows, app, versionBump = false) {
  if (!catalogs[app]) catalogs[app] = {}
  for (const lang of LANGUAGES.map((l) => l.code)) {
    catalogs[app][lang] = buildNestedMessages(rows, app, lang)
  }
  if (versionBump) {
    versions[app] = (versions[app] ?? 1) + 1
  }
  return versions[app]
}

/** Keep shared + active app catalogs in sync after admin edits. */
export function hydrateAppWithShared(rows, app, versionBump = false) {
  hydrateFromRows(rows, SHARED_APP, versionBump && app === SHARED_APP)
  if (app !== SHARED_APP) {
    hydrateFromRows(rows, app, versionBump)
  }
  return versions[app] ?? versions[SHARED_APP]
}

/** Prefill catalogs from mock for all apps. */
export function initFromMock(rows = INITIAL_ROWS) {
  catalogs = {}
  versions = { ...INITIAL_VERSIONS }
  for (const app of APP_CODES) {
    hydrateFromRows(rows, app, false)
  }
}

initFromMock()

export const translations = {
  get catalog() {
    return ensureCatalog(currentApp, currentLang)
  },
}

export default {
  t,
  tSource,
  setLanguage,
  getLanguage,
  setApplication,
  getApplication,
  getVersion,
  loadMessages,
  hydrateFromRows,
  hydrateAppWithShared,
  initFromMock,
  translations,
  SHARED_APP,
}
